import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useGlobalDependencyProvider } from '@teachino/shared-util';

const IDLE_BAR_LEVEL = 0.08;
const MAX_BAR_LEVEL = 1;
const VISUALIZER_VOLUME_GAIN = 8;
const BYTE_MIDPOINT = 128;
const MS_PER_SECOND = 1000;

export type AudioInputDevice = {
  deviceId: string;
  label: string;
};

function clampBarLevel(level: number) {
  return Math.min(MAX_BAR_LEVEL, Math.max(IDLE_BAR_LEVEL, level));
}

export function useSpeechToTextRecording() {
  const globalDi = useGlobalDependencyProvider();
  const config = globalDi.inject('config');
  const defaultMaxDurationSeconds = config.audioRecordingMaxSeconds();

  const isRecording = ref(false);
  const isSupported = ref(
    typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices !== 'undefined' &&
      typeof MediaRecorder !== 'undefined',
  );
  const inputDevices = ref<AudioInputDevice[]>([]);
  const selectedInputDeviceId = ref<string | null>(null);
  const inputVolumeLevel = ref(IDLE_BAR_LEVEL);
  const selectedInputDeviceLabel = ref<string | null>(null);

  let mediaRecorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let maxDurationTimer: ReturnType<typeof setTimeout> | null = null;
  let resolveRecording: ((blob: Blob) => void) | null = null;
  let rejectRecording: ((error: Error) => void) | null = null;
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let sourceNode: MediaStreamAudioSourceNode | null = null;
  let timeDomainData: Uint8Array<ArrayBuffer> | null = null;
  let visualizerFrame: number | null = null;

  function normalizeDeviceLabel(label: string) {
    return label.trim().toLowerCase();
  }

  function findDeviceByLabel(label: string) {
    const normalizedLabel = normalizeDeviceLabel(label);

    return (
      inputDevices.value.find(
        (device) => normalizeDeviceLabel(device.label) === normalizedLabel,
      ) ??
      inputDevices.value.find((device) => {
        const normalizedDeviceLabel = normalizeDeviceLabel(device.label);
        return (
          normalizedDeviceLabel.includes(normalizedLabel) ||
          normalizedLabel.includes(normalizedDeviceLabel)
        );
      }) ??
      null
    );
  }

  function getDefaultDeviceId() {
    const defaultDevice = inputDevices.value.find(
      (device) => device.deviceId === 'default',
    );
    if (defaultDevice) {
      return defaultDevice.deviceId;
    }

    if (inputDevices.value.length === 0) {
      return null;
    }

    return inputDevices.value[0].deviceId;
  }

  function normalizeSelectedInputDevice() {
    if (inputDevices.value.length === 0) {
      selectedInputDeviceId.value = null;
      return;
    }

    if (
      selectedInputDeviceId.value &&
      inputDevices.value.some(
        (device) => device.deviceId === selectedInputDeviceId.value,
      )
    ) {
      return;
    }

    if (selectedInputDeviceLabel.value) {
      const matchingDevice = findDeviceByLabel(selectedInputDeviceLabel.value);
      if (matchingDevice) {
        selectedInputDeviceId.value = matchingDevice.deviceId;
        return;
      }
    }

    selectedInputDeviceId.value = getDefaultDeviceId();
  }

  async function refreshInputDevices() {
    if (!isSupported.value) {
      inputDevices.value = [];
      selectedInputDeviceId.value = null;
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      inputDevices.value = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label,
        }));
      normalizeSelectedInputDevice();
    } catch (error) {
      console.error(error);
      inputDevices.value = [];
      selectedInputDeviceId.value = null;
    }
  }

  function resetVisualizer() {
    inputVolumeLevel.value = IDLE_BAR_LEVEL;
  }

  function stopAudioAnalysis() {
    if (visualizerFrame !== null) {
      cancelAnimationFrame(visualizerFrame);
      visualizerFrame = null;
    }

    if (sourceNode) {
      sourceNode.disconnect();
      sourceNode = null;
    }

    if (analyser) {
      analyser.disconnect();
      analyser = null;
    }

    if (audioContext) {
      void audioContext.close();
      audioContext = null;
    }

    timeDomainData = null;
    resetVisualizer();
  }

  function updateVisualizer() {
    if (!analyser || !timeDomainData) {
      resetVisualizer();
      return;
    }

    const data = timeDomainData;
    analyser.getByteTimeDomainData(data);

    let sumSquares = 0;
    for (const sample of data) {
      const centeredSample = (sample - BYTE_MIDPOINT) / BYTE_MIDPOINT;
      sumSquares += centeredSample * centeredSample;
    }

    const rootMeanSquare = Math.sqrt(sumSquares / data.length);

    inputVolumeLevel.value = clampBarLevel(
      rootMeanSquare * VISUALIZER_VOLUME_GAIN,
    );

    visualizerFrame = requestAnimationFrame(updateVisualizer);
  }

  function startAudioAnalysis(mediaStream: MediaStream) {
    stopAudioAnalysis();

    if (typeof AudioContext === 'undefined') {
      resetVisualizer();
      return;
    }

    audioContext = new AudioContext();
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.82;
    timeDomainData = new Uint8Array(analyser.fftSize);
    sourceNode = audioContext.createMediaStreamSource(mediaStream);
    sourceNode.connect(analyser);

    void audioContext.resume().finally(() => {
      if (audioContext) {
        updateVisualizer();
      }
    });
  }

  function getAudioConstraints(): MediaTrackConstraints | boolean {
    if (
      !selectedInputDeviceId.value ||
      selectedInputDeviceId.value === 'default'
    ) {
      return true;
    }

    return {
      deviceId: {
        exact: selectedInputDeviceId.value,
      },
    };
  }

  async function createAudioStream() {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: getAudioConstraints(),
      });
    } catch (error) {
      await refreshInputDevices();

      if (selectedInputDeviceLabel.value) {
        const matchingDevice = findDeviceByLabel(
          selectedInputDeviceLabel.value,
        );
        if (
          matchingDevice &&
          matchingDevice.deviceId !== selectedInputDeviceId.value
        ) {
          selectedInputDeviceId.value = matchingDevice.deviceId;
          return navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: {
                exact: matchingDevice.deviceId,
              },
            },
          });
        }
      }

      const shouldRetryWithDefaultDevice =
        selectedInputDeviceId.value !== null &&
        selectedInputDeviceId.value !== 'default' &&
        error instanceof DOMException &&
        (error.name === 'NotFoundError' ||
          error.name === 'OverconstrainedError');

      if (!shouldRetryWithDefaultDevice) {
        throw error;
      }

      selectedInputDeviceId.value = getDefaultDeviceId();
      return navigator.mediaDevices.getUserMedia({ audio: true });
    }
  }

  function cleanup() {
    if (maxDurationTimer) {
      clearTimeout(maxDurationTimer);
      maxDurationTimer = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
    }
    stopAudioAnalysis();
    mediaRecorder = null;
    chunks = [];
  }

  async function start(
    maxDurationSeconds = defaultMaxDurationSeconds,
  ): Promise<Blob> {
    if (isRecording.value) {
      throw new Error('Already recording');
    }

    stream = await createAudioStream();
    startAudioAnalysis(stream);
    await refreshInputDevices();

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';

    mediaRecorder = new MediaRecorder(stream, { mimeType });
    chunks = [];

    return new Promise<Blob>((resolve, reject) => {
      resolveRecording = resolve;
      rejectRecording = reject;

      const recorder = mediaRecorder;
      if (!recorder) {
        reject(new Error('MediaRecorder not initialized'));
        return;
      }

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        cleanup();
        isRecording.value = false;
        if (rejectRecording && blob.size === 0) {
          rejectRecording(new DOMException('Recording aborted', 'AbortError'));
          rejectRecording = null;
          resolveRecording = null;
          return;
        }
        if (resolveRecording) {
          resolveRecording(blob);
          resolveRecording = null;
        }
        rejectRecording = null;
      };

      recorder.onerror = () => {
        cleanup();
        isRecording.value = false;
        resolveRecording = null;
        rejectRecording = null;
        reject(new Error('Recording failed'));
      };

      recorder.start();
      isRecording.value = true;

      maxDurationTimer = setTimeout(() => {
        stop();
      }, maxDurationSeconds * MS_PER_SECOND);
    });
  }

  function stop() {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  }

  function abort() {
    chunks = [];
    stop();
  }

  function onDeviceChange() {
    void refreshInputDevices();
  }

  watch(
    selectedInputDeviceId,
    (nextDeviceId) => {
      if (!nextDeviceId) {
        selectedInputDeviceLabel.value = null;
        return;
      }

      const selectedDevice = inputDevices.value.find(
        (device) => device.deviceId === nextDeviceId,
      );
      if (selectedDevice?.label) {
        selectedInputDeviceLabel.value = selectedDevice.label;
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    void refreshInputDevices();
    if (isSupported.value) {
      navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    }
  });

  onUnmounted(() => {
    if (isSupported.value) {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        onDeviceChange,
      );
    }
    if (isRecording.value) {
      stop();
    }
    cleanup();
  });

  return {
    abort,
    defaultMaxDurationSeconds,
    inputVolumeLevel,
    inputDevices,
    isRecording,
    isSupported,
    selectedInputDeviceId,
    start,
    stop,
  };
}
